import { useState, useRef, useEffect } from "react";
import { SUDOKU_PUZZLES } from "@/data/exercises";

export default function Sudoku({ level = "facile", onComplete }) {
  const puzzle = SUDOKU_PUZZLES[level];
  const { size, box, given, solution } = puzzle;
  const [grid, setGrid] = useState([]);
  const [selected, setSelected] = useState(null); // [r,c]
  const wrong = useRef(0);
  const start = useRef(Date.now());
  const done = useRef(false);

  useEffect(() => {
    setGrid(given.map((row) => [...row]));
    done.current = false;
    start.current = Date.now();
    wrong.current = 0;
    setSelected(null);
  }, [level]); // eslint-disable-line react-hooks/exhaustive-deps

  const isGiven = (r, c) => given[r][c] !== 0;

  const checkDone = (g) => {
    for (let r = 0; r < size; r++)
      for (let c = 0; c < size; c++) if (g[r][c] !== solution[r][c]) return false;
    return true;
  };

  const place = (num) => {
    if (!selected) return;
    const [r, c] = selected;
    if (isGiven(r, c)) return;
    const g = grid.map((row) => [...row]);
    g[r][c] = num;
    setGrid(g);
    if (num !== solution[r][c]) wrong.current += 1;
    if (checkDone(g) && !done.current) {
      done.current = true;
      const duration = Math.round((Date.now() - start.current) / 1000);
      const acc = Math.max(40, 100 - wrong.current * 8);
      setTimeout(() => onComplete({ score: acc, accuracy: acc, duration }), 500);
    }
  };

  const cellSize = size === 4 ? "w-16 h-16 text-3xl" : "w-12 h-12 sm:w-14 sm:h-14 text-2xl";

  return (
    <div className="w-full max-w-xl mx-auto text-center" data-testid={`sudoku-game-${level}`}>
      <p className="text-2xl font-patient font-bold text-gray-700 mb-6">
        Complétez la grille : chaque ligne, colonne et bloc contient les chiffres 1 à {size}
      </p>

      <div className="inline-block bg-emerald-700 p-1.5 rounded-2xl mb-8">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`, gap: 2 }}>
          {grid.map((row, r) =>
            row.map((val, c) => {
              const fixed = isGiven(r, c);
              const sel = selected && selected[0] === r && selected[1] === c;
              const correct = val !== 0 && val === solution[r][c];
              const bad = val !== 0 && !fixed && val !== solution[r][c];
              const thickR = (r + 1) % box.rows === 0 && r !== size - 1;
              const thickC = (c + 1) % box.cols === 0 && c !== size - 1;
              return (
                <button
                  key={`${r}-${c}`}
                  data-testid={`sudoku-cell-${r}-${c}`}
                  onClick={() => !fixed && setSelected([r, c])}
                  className={`${cellSize} font-patient font-black flex items-center justify-center rounded-md transition-colors duration-150 ${
                    fixed
                      ? "bg-emerald-50 text-emerald-900"
                      : sel
                      ? "bg-amber-200 text-gray-900 ring-4 ring-amber-400"
                      : correct
                      ? "bg-white text-emerald-700"
                      : bad
                      ? "bg-red-50 text-red-600"
                      : "bg-white text-gray-800"
                  }`}
                  style={{
                    marginBottom: thickR ? 4 : 0,
                    marginRight: thickC ? 4 : 0,
                  }}
                >
                  {val !== 0 ? val : ""}
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="flex justify-center gap-3 flex-wrap">
        {Array.from({ length: size }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            onClick={() => place(n)}
            data-testid={`sudoku-num-${n}`}
            className="w-16 h-16 rounded-2xl bg-emerald-700 text-white font-patient font-black text-3xl active:scale-90 transition-transform duration-150 border-4 border-emerald-800/20"
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
